import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { WifiOff, RefreshCw } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function Offline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline) {
      window.location.reload();
    }
  }, [isOnline]);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <>
      <SEO 
        title="Offline - HiStage"
        description="You are currently offline"
      />
      
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <WifiOff className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl">You're Offline</CardTitle>
            <CardDescription>
              It looks like you've lost your internet connection
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Don't worry! Some features may still be available from your cached content. 
              Check your connection and try again.
            </p>
            
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">While offline, you can:</h3>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>View previously loaded pages</li>
                <li>Browse cached streams and content</li>
                <li>Access your profile information</li>
              </ul>
            </div>

            <Button 
              onClick={handleRetry} 
              className="w-full"
              variant="default"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry Connection
            </Button>

            {isOnline && (
              <p className="text-sm text-green-600 dark:text-green-400 text-center font-medium">
                Connection restored! Reloading...
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
